// safety/incidents-and-injuries/components/IncidentForm.tsx
// Shared core-fields form used by both the New Incident page and the
// Edit drawer on the detail page. Controlled via `value` + `onChange`.
import React from 'react';
import { Combobox } from '../../../../components/ui/combobox';
import { MultiSelect } from '../../../../components/ui/multi-select';
import type { Incident, SafetyLists, EmployeeRef, EntityRef } from '../../../../types/incident.types';

const CardBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <h3 className="text-base font-semibold mb-4">{title}</h3>
        {children}
    </div>
);

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
    <label className="text-sm font-medium">
        {children} {required && <span className="text-destructive">*</span>}
    </label>
);

const inputCls = 'w-full h-11 rounded-lg border border-border bg-card px-3 text-sm';
const areaCls = 'w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm';

// List options keyed by their own label. If the stored value was removed from
// the managed list (Safety → Settings), keep it selectable/labelled so editing
// never silently drops or mislabels it.
const snapshotOptions = (items: string[], current?: string) => {
    const opts = items.map((o) => ({ value: o, label: o }));
    if (current && !items.includes(current)) opts.unshift({ value: current, label: current });
    return opts;
};

const YesNo: React.FC<{ value: boolean | null; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
    <div className="flex gap-2">
        {[true, false].map((b) => (
            <button
                key={String(b)}
                type="button"
                onClick={() => onChange(b)}
                className={`h-10 px-6 rounded-lg text-sm font-medium border ${
                    value === b ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-card hover:bg-accent'
                }`}
            >
                {b ? 'Yes' : 'No'}
            </button>
        ))}
    </div>
);

export const IncidentForm: React.FC<{
    value: Partial<Incident>;
    onChange: (patch: Partial<Incident>) => void;
    lists: SafetyLists;
    employees: EmployeeRef[];
    entities: EntityRef[];
}> = ({ value: d, onChange: set, lists, employees, entities }) => (
    <>
        <CardBlock title="Incident Details">
            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label required>Employee Name</Label>
                        {employees.length > 0 ? (
                            <div className="mt-1.5">
                                <Combobox
                                    options={employees.map((e) => ({ label: e.name, value: e.name }))}
                                    value={d.employeeName || undefined}
                                    onChange={(value) => {
                                        const emp = employees.find((x) => x.name === value);
                                        set({ employeeName: value || '', employeeId: emp?.id ?? '' });
                                    }}
                                    placeholder="Select employee..."
                                    searchPlaceholder="Search employees..."
                                    emptyMessage="No employees found."
                                    className="w-full"
                                />
                            </div>
                        ) : (
                            <input value={d.employeeName} onChange={(e) => set({ employeeName: e.target.value })} placeholder="Enter employee name" className={`${inputCls} mt-1.5`} />
                        )}
                    </div>
                    <div>
                        <Label>Employer Entity</Label>
                        <div className="mt-1.5">
                            <Combobox
                                options={snapshotOptions(entities.map((e) => e.name), d.entityName)}
                                value={d.entityName || undefined}
                                onChange={(value) => {
                                    const ent = entities.find((x) => x.name === value);
                                    set({ entityName: value || '', entityId: ent?.id ?? '' });
                                }}
                                placeholder="Select entity..."
                                searchPlaceholder="Search entities..."
                                emptyMessage="No entities found."
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Project / Site</Label>
                        <input value={d.project} onChange={(e) => set({ project: e.target.value })} placeholder="Enter project name" className={`${inputCls} mt-1.5`} />
                    </div>
                    <div>
                        <Label>Supervisor</Label>
                        {employees.length > 0 ? (
                            <div className="mt-1.5">
                                <Combobox
                                    options={employees.map((e) => ({ label: e.name, value: e.name }))}
                                    value={d.supervisor || undefined}
                                    onChange={(value) => set({ supervisor: value || '' })}
                                    placeholder="Select supervisor..."
                                    searchPlaceholder="Search users..."
                                    emptyMessage="No users found."
                                />
                            </div>
                        ) : (
                            <input value={d.supervisor} onChange={(e) => set({ supervisor: e.target.value })} placeholder="Enter supervisor name" className={`${inputCls} mt-1.5`} />
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label>RTW Coordinator</Label>
                        {employees.length > 0 ? (
                            <div className="mt-1.5">
                                <Combobox
                                    options={employees.map((e) => ({ label: e.name, value: e.name }))}
                                    value={d.rtwCoordinator || undefined}
                                    onChange={(value) => set({ rtwCoordinator: value || '' })}
                                    placeholder="Select RTW coordinator..."
                                    searchPlaceholder="Search users..."
                                    emptyMessage="No users found."
                                />
                            </div>
                        ) : (
                            <input value={d.rtwCoordinator} onChange={(e) => set({ rtwCoordinator: e.target.value })} placeholder="Enter RTW coordinator name" className={`${inputCls} mt-1.5`} />
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label required>Incident Date</Label>
                        <input type="date" value={d.date} onChange={(e) => set({ date: e.target.value })} className={`${inputCls} mt-1.5`} />
                    </div>
                    <div>
                        <Label>Incident Time</Label>
                        <input type="time" value={d.time} onChange={(e) => set({ time: e.target.value })} className={`${inputCls} mt-1.5`} />
                    </div>
                </div>
                <div>
                    <Label>Witnesses</Label>
                    {employees.length > 0 ? (
                        <div className="mt-1.5">
                            <MultiSelect
                                options={employees.map((e) => ({ value: e.name, label: e.name }))}
                                selected={d.witnesses ? d.witnesses.split(', ').filter(Boolean) : []}
                                onChange={(sel) => set({ witnesses: sel.join(', ') })}
                                placeholder="Select witnesses..."
                                emptyMessage="No employees found."
                                maxDisplayTags={3}
                            />
                        </div>
                    ) : (
                        <input value={d.witnesses} onChange={(e) => set({ witnesses: e.target.value })} placeholder="Name witnesses separated by commas" className={`${inputCls} mt-1.5`} />
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <Label>Cause of Injury</Label>
                        <div className="mt-1.5">
                            <Combobox
                                options={snapshotOptions(lists.causeOfInjury, d.cause)}
                                value={d.cause || undefined}
                                onChange={(value) => set({ cause: value || '' })}
                                placeholder="Select..."
                                searchPlaceholder="Search causes..."
                                emptyMessage="No options found."
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Body Part Injured</Label>
                        <div className="mt-1.5">
                            <Combobox
                                options={snapshotOptions(lists.bodyPart, d.bodyPart)}
                                value={d.bodyPart || undefined}
                                onChange={(value) => set({ bodyPart: value || '' })}
                                placeholder="Select..."
                                searchPlaceholder="Search body parts..."
                                emptyMessage="No options found."
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Nature of Injury</Label>
                        <div className="mt-1.5">
                            <Combobox
                                options={snapshotOptions(lists.natureOfInjury, d.nature)}
                                value={d.nature || undefined}
                                onChange={(value) => set({ nature: value || '' })}
                                placeholder="Select..."
                                searchPlaceholder="Search natures of injury..."
                                emptyMessage="No options found."
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <Label>Injury Description</Label>
                    <textarea rows={4} value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="Describe the incident in detail..." className={`${areaCls} mt-1.5`} />
                </div>
            </div>
        </CardBlock>

        <CardBlock title="First Aid">
            <div className="space-y-4">
                <div>
                    <Label>Was First Aid Applied?</Label>
                    <div className="mt-1.5">
                        <YesNo value={d.firstAidApplied ?? null} onChange={(v) => set({ firstAidApplied: v })} />
                    </div>
                </div>
                <div>
                    <Label>First Aider Name</Label>
                    <input value={d.firstAiderName} onChange={(e) => set({ firstAiderName: e.target.value })} placeholder="Enter first aider name" className={`${inputCls} mt-1.5`} />
                </div>
                <div>
                    <Label>Treatment Administered</Label>
                    <textarea rows={4} value={d.firstAidTreatment} onChange={(e) => set({ firstAidTreatment: e.target.value })} placeholder="Describe first aid treatment..." className={`${areaCls} mt-1.5`} />
                </div>
            </div>
        </CardBlock>

        <CardBlock title="Initial Outcome">
            <div className="space-y-4">
                <div>
                    <Label>Stayed At Work?</Label>
                    <div className="mt-1.5">
                        <YesNo value={d.stayedAtWork ?? null} onChange={(v) => set({ stayedAtWork: v })} />
                    </div>
                </div>
                <div>
                    <Label>Hospitalised?</Label>
                    <div className="mt-1.5">
                        <YesNo value={d.hospitalised ?? null} onChange={(v) => set({ hospitalised: v })} />
                    </div>
                </div>
                <div>
                    <Label>Fit To Return To Work?</Label>
                    <div className="mt-1.5">
                        <YesNo value={d.fitToReturn ?? null} onChange={(v) => set({ fitToReturn: v })} />
                    </div>
                </div>
            </div>
        </CardBlock>
    </>
);

export default IncidentForm;
